import * as React from 'react';
import * as LabelPrimitive from '@radix-ui/react-label';
import { Slot } from '@radix-ui/react-slot';
import {
  Controller,
  ControllerProps,
  FieldPath,
  FieldValues,
  FormProvider,
  useFormContext,
} from 'react-hook-form';

import { Label } from '@/lib/components/ui/label';
import { cn } from '@/lib/utils/helpers/cn';

const Form = FormProvider;

type FormFieldContextValue<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  name: TName;
};

const FormFieldContext = React.createContext<FormFieldContextValue>(
  {} as FormFieldContextValue
);

const FormField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  ...props
}: ControllerProps<TFieldValues, TName>) => {
  return (
    <FormFieldContext.Provider value={{ name: props.name }}>
      <Controller {...props} />
    </FormFieldContext.Provider>
  );
};

const useFormField = () => {
  const fieldContext = React.useContext(FormFieldContext);
  const itemContext = React.useContext(FormItemContext);
  const { getFieldState, formState } = useFormContext();

  const fieldState = getFieldState(fieldContext.name, formState);

  if (!fieldContext) {
    throw new Error('useFormField should be used within <FormField>');
  }

  const { id } = itemContext;

  return {
    id,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    ...fieldState,
  };
};

type FormItemContextValue = {
  id: string;
};

const FormItemContext = React.createContext<FormItemContextValue>(
  {} as FormItemContextValue
);

function FormItem({ className, ...props }: React.ComponentProps<'div'>) {
  const id = React.useId();

  return (
    <FormItemContext.Provider value={{ id }}>
      <div
        data-slot="form-item"
        className={cn('grid gap-2', className)}
        {...props}
      />
    </FormItemContext.Provider>
  );
}

// const FormItem = ({ className, ...props }: any) => {
//   const id = React.useId();

//   return (
//     <FormItemContext.Provider value={{ id }}>
//       <div className={cn('', className)} {...props} />
//     </FormItemContext.Provider>
//   );
// };
// FormItem.displayName = 'FormItem';

function FormLabel({
  className,
  ...props
}: React.ComponentProps<typeof LabelPrimitive.Root>) {
  const { error, formItemId } = useFormField();

  return (
    <Label
      data-slot="form-label"
      data-error={!!error}
      className={cn(
        error && 'text-destructive',
        'ring-offset-background cursor-default text-sm font-medium text-gray-500 transition-colors dark:text-gray-200',
        className
      )}
      htmlFor={formItemId}
      {...props}
    />
  );
}

// const FormLabel = ({ className, ref, ...props }: any) => {
//   const { error, formItemId } = useFormField();

//   return (
//     <Label
//       ref={ref}
//       className={cn(
//         error && 'text-destructive',
//         'ring-offset-background cursor-default text-sm font-medium text-gray-500 transition-colors dark:text-gray-200',
//         className
//       )}
//       htmlFor={formItemId}
//       {...props}
//     />
//   );
// };
// FormLabel.displayName = 'FormLabel';

function FormControl({ ...props }: React.ComponentProps<typeof Slot>) {
  const { error, formItemId, formDescriptionId, formMessageId } =
    useFormField();

  return (
    <Slot
      data-slot="form-control"
      id={formItemId}
      aria-describedby={
        !error
          ? `${formDescriptionId}`
          : `${formDescriptionId} ${formMessageId}`
      }
      aria-invalid={!!error}
      {...props}
    />
  );
}

// const FormControl = ({ className, ref, ...props }: any) => {
//   const { error, formItemId, formDescriptionId, formMessageId } =
//     useFormField();

//   return (
//     <Slot
//       ref={ref}
//       id={formItemId}
//       aria-describedby={
//         !error
//           ? `${formDescriptionId}`
//           : `${formDescriptionId} ${formMessageId}`
//       }
//       aria-invalid={!!error}
//       className={cn('', className)}
//       {...props}
//     />
//   );
// };
// FormControl.displayName = 'FormControl';

function FormDescription({ className, ...props }: React.ComponentProps<'p'>) {
  const { formDescriptionId } = useFormField();

  return (
    <p
      data-slot="form-description"
      id={formDescriptionId}
      className={cn('text-muted-foreground text-sm', className)}
      {...props}
    />
  );
}

// const FormDescription = ({ className, ref, ...props }: any) => {
//   const { formDescriptionId } = useFormField();

//   return (
//     <p
//       id={formDescriptionId}
//       className={cn('text-muted-foreground text-sm', className)}
//       ref={ref}
//       {...props}
//     />
//   );
// };
// FormDescription.displayName = 'FormDescription';

function FormMessage({ className, ...props }: React.ComponentProps<'p'>) {
  const { error, formMessageId } = useFormField();
  const body = error ? String(error?.message ?? '') : props.children;

  if (!body) {
    return null;
  }

  return (
    <p
      data-slot="form-message"
      id={formMessageId}
      className={cn('text-destructive text-sm', className)}
      {...props}
    >
      {body}
    </p>
  );
}

// const FormMessage = ({ className, ref, children, ...props }: any) => {
//   const { error, formMessageId } = useFormField();
//   const body = error ? String(error?.message) : children;

//   if (!body) {
//     return null;
//   }

//   return (
//     <p
//       id={formMessageId}
//       className={cn('text-destructive m-0 p-0 text-sm font-medium', className)}
//       ref={ref}
//       {...props}
//     >
//       {body}
//     </p>
//   );
// };
// FormMessage.displayName = 'FormMessage';

export {
  useFormField,
  Form,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
  FormField,
};
